/**
 * SYSTELOS MONITOR - Serviço de Comunicações (Frontend)
 *
 * @version 2.1.0
 * @date 26/01/2026
 * ADD: Suporte a DEC SEFAZ-SP além de DJE
 * ADD: Filtro por origem (DJE/DEC_SEFAZ)
 * ADD: Buscar comunicações por cliente
 * FIX: Adicionado ordenar e direcao nos filtros
 */

import api from './api'

// Tipos de origem
export type OrigemComunicacao = 'DJE' | 'DEC_SEFAZ'

// Tipos de comunicação
export type TipoComunicacao =
  | 'INTIMACAO'
  | 'CITACAO'
  | 'INTIMACAO_FISCAL'
  | 'NOTIFICACAO_FISCAL'
  | 'AVISO_FISCAL'
  | 'COMUNICADO_FISCAL'

// Status
export type StatusComunicacao = 'NOVA' | 'PENDENTE' | 'CIENCIA_DADA' | 'ARQUIVADA'

// Prioridade
export type Prioridade = 'URGENTE' | 'ALTA' | 'MEDIA' | 'BAIXA'

// Interface principal
export interface Comunicacao {
  id: string
  cliente_id: string

  // Identificação
  origem: OrigemComunicacao
  id_externo: string  // ID único da fonte (DJE ou SEFAZ)

  // Campos DJE
  numero_processo?: string
  tribunal?: string
  orgao_julgador?: string

  // Campos comuns
  tipo: TipoComunicacao
  assunto?: string
  conteudo?: string

  // Datas
  data_disponibilizacao: string
  data_fim_ciencia?: string
  prazo_resposta?: string

  // Status
  status: StatusComunicacao
  prioridade: Prioridade
  lida: boolean

  // Ciência
  ciencia_em?: string
  ciencia_por?: string

  // Anexos
  tem_anexo: boolean
  anexos?: { nome: string; url: string; tamanho: number }[]

  // Notificação
  notificado: boolean
  notificado_em?: string

  // Timestamps
  created_at: string
  updated_at: string

  // Relações (opcionais)
  cliente?: {
    id: string
    razao_social: string
    nome_fantasia?: string
    cnpj: string
  }
}

// Filtros
export interface ComunicacaoFiltros {
  page?: number
  limit?: number
  busca?: string
  origem?: OrigemComunicacao | 'todos'
  status?: StatusComunicacao | 'todos'
  prioridade?: Prioridade | 'todos'
  tipo?: TipoComunicacao | 'todos'
  cliente_id?: string
  lida?: boolean
  hoje?: boolean
  // Ordenação
  ordenar?: string
  direcao?: 'asc' | 'desc'
}

// Estatísticas
export interface ComunicacaoStats {
  total: number
  pendentes: number
  ciencia_dada: number
  arquivadas: number
  urgentes: number
  hoje: number
  nao_lidas: number
  // Por origem
  total_dje: number
  total_dec: number
  pendentes_dje: number
  pendentes_dec: number
}

// Resposta paginada
interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

const comunicacoesService = {
  // Listar comunicações com filtros
  async listar(filtros?: ComunicacaoFiltros): Promise<PaginatedResponse<Comunicacao>> {
    const params: any = { ...filtros }

    // Converter 'todos' para undefined
    if (params.origem === 'todos') delete params.origem
    if (params.status === 'todos') delete params.status
    if (params.prioridade === 'todos') delete params.prioridade
    if (params.tipo === 'todos') delete params.tipo

    const response = await api.get('/comunicacoes', { params })
    return response.data
  },

  // Buscar comunicação por ID
  async buscarPorId(id: string): Promise<Comunicacao> {
    const response = await api.get(`/comunicacoes/${id}`)
    return response.data.data || response.data
  },

  // Listar comunicações de um cliente específico
  async listarPorCliente(clienteId: string, filtros?: Partial<ComunicacaoFiltros>): Promise<PaginatedResponse<Comunicacao>> {
    const response = await api.get(`/clientes/${clienteId}/comunicacoes`, { params: filtros })
    return response.data
  },

  // Dar ciência
  async darCiencia(id: string, dados: { ciencia_por: string }): Promise<Comunicacao> {
    const response = await api.put(`/comunicacoes/${id}/ciencia`, dados)
    return response.data.data || response.data
  },

  // Arquivar
  async arquivar(id: string): Promise<Comunicacao> {
    const response = await api.put(`/comunicacoes/${id}/arquivar`)
    return response.data.data || response.data
  },

  // Marcar como lida
  async marcarLida(id: string): Promise<Comunicacao> {
    const response = await api.put(`/comunicacoes/${id}/lida`)
    return response.data.data || response.data
  },

  // Estatísticas
  async stats(): Promise<ComunicacaoStats> {
    const response = await api.get('/comunicacoes/stats')
    return response.data.data || response.data
  },

  // Alertas (comunicações urgentes/próximas do prazo)
  async alertas(): Promise<Comunicacao[]> {
    const response = await api.get('/comunicacoes/alertas')
    return response.data.data || response.data
  },
}

export default comunicacoesService

